import * as React from "react";
import * as Toast from "@radix-ui/react-toast";

import * as styles from "./Snackbar.css";

interface SnackbarRegistryContextValue {
  register: (id: string) => void;
  unregister: (id: string) => void;
  isEarliest: (id: string) => boolean;
  hasMultiple: boolean;
}

const SnackbarRegistryContext =
  React.createContext<SnackbarRegistryContextValue | null>(null);

export const useSnackbarRegistry = () => {
  return React.useContext(SnackbarRegistryContext);
};

export interface SnackbarProviderProps {
  children: React.ReactNode;
  swipeDirection?: "right" | "left" | "up" | "down";
  swipeThreshold?: number;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
  swipeDirection = "up",
  swipeThreshold = 50,
}) => {
  const [registeredIds, setRegisteredIds] = React.useState<string[]>([]);
  const registeredIdsRef = React.useRef<string[]>([]);

  // Keep ref in sync with state for stable callback access
  React.useEffect(() => {
    registeredIdsRef.current = registeredIds;
  }, [registeredIds]);

  const register = React.useCallback((id: string) => {
    setRegisteredIds((prev) => {
      if (prev.includes(id)) return prev;
      const newIds = [...prev, id];
      registeredIdsRef.current = newIds;
      return newIds;
    });
  }, []);

  const unregister = React.useCallback((id: string) => {
    setRegisteredIds((prev) => {
      const newIds = prev.filter((existingId) => existingId !== id);
      registeredIdsRef.current = newIds;
      return newIds;
    });
  }, []);

  const isEarliest = React.useCallback((id: string) => {
    const ids = registeredIdsRef.current;
    return ids.length > 0 && ids[0] === id;
  }, []);

  const hasMultiple = registeredIds.length > 1;

  const contextValue = React.useMemo(
    () => ({ register, unregister, isEarliest, hasMultiple }),
    [register, unregister, isEarliest, hasMultiple]
  );

  return (
    <Toast.Provider
      swipeDirection={swipeDirection}
      swipeThreshold={swipeThreshold}
    >
      <SnackbarRegistryContext.Provider value={contextValue}>
        {children}
      </SnackbarRegistryContext.Provider>
      <Toast.Viewport className={styles.toastViewport} />
    </Toast.Provider>
  );
};
